import { PureComponent, createContext } from "react";
import { connect } from "react-redux";
import { animateScroll as scroll } from "react-scroll";
import { uniqueId } from "lodash";

import { withMapContext } from "../MapContext";
import withContentHeight from "../../Utils/withContentHeight";
import { getDisplayName } from "../../Utils/getDisplayName";
import { withStickyElementsHeight } from "../../Utils/StickyElementsHeight";
import { withResponsiveFlag } from "../../Utils/ResponsiveQueries";

import { setMapPanelElement } from "../../../reducers/map/panels";

export const MapPanelSizingContext = createContext({});

export default function withMapPanelSizing(WrappedComponent) {
  class WithMapPanelSizing extends PureComponent {
    static displayName = `WithMapPanelSizing(${getDisplayName(WrappedComponent)})`;

    state = {
      height: "auto"
    };

    constructor(props) {
      super(props);

      this.containerPanelRef = this.containerPanelRef.bind(this);
      this.innerPanelRef = this.innerPanelRef.bind(this);
      this.reprocessPanelHeight = this.reprocessPanelHeight.bind(this);
      this.forceInnerPanelScrollRefresh = this.forceInnerPanelScrollRefresh.bind(this);
      this.handleContentHeightChange = this.handleContentHeightChange.bind(this);
      this.scrollPanelTo = this.scrollPanelTo.bind(this);
    }

    get actionsHeight() {
      if (!this.containerPanel) return 0;

      const actions = this.containerPanel.querySelector(".map-panel-actions");

      if (!actions) return 0;

      return actions.offsetHeight;
    }

    get toggleButtonHeight() {
      if (!this.containerPanel) return 0;

      const toggleButton = this.containerPanel.querySelector(
        ".map-panel-mobile-toggle-button"
      );

      if (!toggleButton) return 0;

      return toggleButton.offsetHeight;
    }

    // The desktop panel is absolutely positioned at the top of the map sidebar container,
    // so its own top offset is the exact boundary below the sticky elements. Measuring it
    // is more reliable than summing the sticky elements heights read from the store,
    // which are published asynchronously by the header components and can therefore be
    // out of date, or lower than the panel's real offset, when the panel is sized.
    //
    // Getting this wrong makes the panel extend below the viewport, and since the map
    // page disables the document scrolling, the overflowing part of the panel becomes
    // unreachable : the panel is tall enough to fit its whole content, so its inner
    // container has nothing left to scroll to bring that part back into view.
    //
    // A panel that is not the shown one is hidden with `display: none` and therefore has
    // no layout box at all, so its rect is empty and measuring it would size the panel to
    // the whole window height. We fall back to the sticky elements heights in that case,
    // and the panel is measured again when it is shown back, see `componentDidUpdate`.
    //
    // The mobile panel is a bottom sheet whose top offset moves along with its open
    // state, so it can't be measured this way and keeps relying on the sticky elements.
    //
    get availableHeight() {
      const { isDesktop, getStickyElementsHeight } = this.props;

      if (isDesktop && this.isContainerPanelLaidOut) {
        return window.innerHeight - this.containerPanel.getBoundingClientRect().top;
      }

      const stickyElementsHeight = getStickyElementsHeight({
        ignoreTopNavBar: !isDesktop,
        ignoreAlert: !isDesktop,
        ignoreStickyTitleBar: true
      });

      return window.innerHeight - stickyElementsHeight;
    }

    // `offsetParent` is null as soon as the element, or one of its ancestors, is hidden
    // with `display: none`, which is what tells us the panel can't be measured yet.
    //
    get isContainerPanelLaidOut() {
      return Boolean(this.containerPanel && this.containerPanel.offsetParent);
    }

    containerPanelRef(containerPanel) {
      this.containerPanel = containerPanel;
      if (!containerPanel) return;

      const { setMapPanelElement } = this.props;
      setMapPanelElement(containerPanel);

      this.reprocessPanelHeight();
    }

    innerPanelRef(innerPanel) {
      this.innerPanel = innerPanel;
      if (!innerPanel) return;

      if (!innerPanel.id) {
        innerPanel.id = uniqueId("map-inner-panel");
      }

      this.props.setContentHeightContainer(
        this.innerPanel,
        this.handleContentHeightChange
      );
    }

    definePanelSize(containerHeight, contentHeight) {
      const { availableHeight } = this;

      if (contentHeight > containerHeight && contentHeight > availableHeight) {
        return "auto";
      } else {
        return availableHeight;
      }
    }

    handleContentHeightChange(contentHeight) {
      this.contentHeight = contentHeight;
      this.reprocessPanelHeight();
    }

    reprocessPanelHeight() {
      setTimeout(() => {
        if (this.isUnmounted || !this.containerPanel || !this.contentHeight) return;

        const { contentHeight } = this;
        const containerHeight = this.containerPanel.clientHeight;
        const height = this.definePanelSize(containerHeight, contentHeight);

        this.scrollTopBeforeRefresh = this.innerPanel && this.innerPanel.scrollTop;

        this.setState(
          { height, containerHeight, contentHeight },
          this.forceInnerPanelScrollRefresh
        );
      }, 0);
    }

    // This is needed for some reason to force the inner panel scroll to be
    // refreshed, at least in Firefox 61.
    //
    forceInnerPanelScrollRefresh() {
      const { innerPanel, scrollTopBeforeRefresh } = this;

      if (!innerPanel) return;

      const { isDesktop } = this.props;
      const { availableHeight } = this;
      const { style } = innerPanel;

      const offsetHeight = isDesktop ? this.actionsHeight : this.toggleButtonHeight;

      style.height = isDesktop
        ? `${availableHeight - offsetHeight}px`
        : `calc(100% - ${offsetHeight}px)`;

      if (scrollTopBeforeRefresh) {
        innerPanel.scrollTop = scrollTopBeforeRefresh;
        this.scrollTopBeforeRefresh = null;
      }
    }

    scrollPanelTo(element) {
      const { innerPanel } = this;
      const containerY = element.offsetTop;

      scroll.scrollTo(containerY, {
        duration: 300,
        containerId: innerPanel.id
      });
    }

    componentDidMount() {
      window.addEventListener("scroll", this.reprocessPanelHeight);
      window.addEventListener("resize", this.reprocessPanelHeight);

      this.sizeListenersBound = true;
    }

    componentWillUnmount() {
      this.isUnmounted = true;

      if (this.sizeListenersBound) {
        window.removeEventListener("scroll", this.reprocessPanelHeight);
        window.removeEventListener("resize", this.reprocessPanelHeight);

        this.sizeListenersBound = false;
      }
    }

    componentDidUpdate(prevProps) {
      const { affixHeaderOffsetTop, pushableContainersOffset, isShown } = this.props;

      // A hidden panel can only be sized from the sticky elements heights, so its real
      // height has to be processed again as soon as it gets a layout box back.
      const hasBeenShownBack = !prevProps.isShown && isShown;

      if (
        prevProps.affixHeaderOffsetTop !== affixHeaderOffsetTop ||
        prevProps.pushableContainersOffset !== pushableContainersOffset ||
        hasBeenShownBack
      ) {
        this.reprocessPanelHeight();
      }
    }

    render() {
      const { containerPanelRef, innerPanelRef, innerPanel, props, scrollPanelTo } = this;

      const { height, contentHeight, containerHeight } = this.state;

      return (
        <MapPanelSizingContext.Provider value={{ scrollPanelTo, innerPanel }}>
          <WrappedComponent
            {...{
              ...props,
              containerPanelRef,
              innerPanelRef,
              height,
              contentHeight,
              containerHeight
            }}
          >
            {props.children}
          </WrappedComponent>
        </MapPanelSizingContext.Provider>
      );
    }
  }

  return withResponsiveFlag(
    withMapContext(
      withStickyElementsHeight(
        withContentHeight(
          connect(mapStateToProps, mapDispatchToProps)(WithMapPanelSizing)
        )
      )
    )
  );
}

function mapStateToProps(state, ownProps) {
  const { mapKey, id } = ownProps;
  const { isShown } = state.map[mapKey].panels.activePanels[id] || {};
  const { pushableContainersOffset, affixHeaderOffsetTop } = state.styling;

  return { isShown, pushableContainersOffset, affixHeaderOffsetTop };
}

function mapDispatchToProps(dispatch, ownProps) {
  const { mapKey, id } = ownProps;

  return {
    setMapPanelElement: element => dispatch(setMapPanelElement(mapKey, id, element))
  };
}
